import { useAuth0 } from "@auth0/auth0-react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { Link, Outlet, useLocation } from "react-router-dom";
import logo from "@/assets/indiqr-logo.png";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Megaphone,
  BarChart3,
  QrCode,
  LogOut,
  Menu,
  X,
  Settings,
  Store,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { LgpdFooter } from "@/components/shared/LgpdFooter";

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
}

const influencerNav: NavItem[] = [
  { label: "Campanhas", href: "/campanhas", icon: Megaphone },
  { label: "Conversões", href: "/conversoes", icon: BarChart3 },
  { label: "Resgates", href: "/resgates", icon: Wallet },
];

const sellerNav: NavItem[] = [
  { label: "Vendas", href: "/vender", icon: QrCode },
  { label: "Resgates", href: "/resgates", icon: Wallet },
  { label: "Conversões", href: "/conversoes", icon: BarChart3 },
];

export default function AppLayout() {
  const { user, logout } = useAuth0();
  const { data: currentUser } = useCurrentUser();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Usa roles da API (/me) que já combina DB + token Auth0
  const roles = currentUser?.roles ?? [];

  const dashboardHref = currentUser?.company_id
    ? `/empresas/${currentUser.company_id}`
    : "/aguardando";

  const adminNav: NavItem[] = [
    { label: "Dashboard", href: dashboardHref, icon: LayoutDashboard },
    { label: "Campanhas", href: "/campanhas", icon: Megaphone },
    { label: "Conversões", href: "/conversoes", icon: BarChart3 },
    { label: "Resgates", href: "/resgates", icon: Wallet },
    { label: "Loja", href: "/loja", icon: Store },
    { label: "Configurações", href: "/configuracoes", icon: Settings },
  ];

  let nav = sellerNav;
  if (roles.includes("indiqr-admin")) nav = adminNav;
  else if (roles.includes("indiqr-influenciador")) nav = influencerNav;

  const roleBadge = roles.includes("indiqr-admin")
    ? "Admin"
    : roles.includes("indiqr-influenciador")
    ? "Influenciador"
    : "Vendedor";

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar — desktop */}
      <aside className="hidden md:flex w-64 flex-col border-r border-sidebar-border bg-sidebar">
        <div className="p-6">
          <img src={logo} alt="IndiQR" className="h-12 w-auto" />
        </div>

        <nav className="flex-1 px-3 space-y-1">
          {nav.map((item) => {
            const active = location.pathname.startsWith(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  active
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                )}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-sidebar-border p-4 space-y-3">
          <div className="text-xs space-y-1">
            <p className="font-medium text-sidebar-foreground truncate">{user?.email}</p>
            <span className="inline-block rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
              {roleBadge}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 text-sidebar-foreground/70"
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          >
            <LogOut className="h-4 w-4" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="flex flex-1 flex-col">
        <header className="flex md:hidden items-center justify-between border-b border-border px-4 py-3">
          <img src={logo} alt="IndiQR" className="h-11 w-auto" />
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </header>

        {/* Mobile nav dropdown */}
        {mobileOpen && (
          <div className="md:hidden border-b border-border bg-card px-4 py-2 space-y-1">
            {nav.map((item) => {
              const active = location.pathname.startsWith(item.href);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium",
                    active ? "bg-sidebar-accent text-sidebar-accent-foreground" : "text-muted-foreground"
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 text-muted-foreground mt-2"
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            >
              <LogOut className="h-4 w-4" />
              Sair
            </Button>
          </div>
        )}

        <main className="flex-1 p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
        <LgpdFooter />
      </div>
    </div>
  );
}
